import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Box,
  Grid,
  Card,
  CardContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  uploadSection: {
    marginBottom: theme.spacing(3),
  },
  textArea: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    marginBottom: theme.spacing(3),
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  validChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  invalidChip: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
}));

const VerifyContacts = () => {
  const classes = useStyles();
  const [phoneNumbers, setPhoneNumbers] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0 });

  const handleVerify = async () => {
    if (!phoneNumbers.trim()) {
      toast.error("Por favor ingresa al menos un número de teléfono");
      return;
    }

    // Parse phone numbers (one per line or comma separated)
    const numbers = phoneNumbers
      .split(/[\n,]/)
      .map((num) => num.trim())
      .filter((num) => num.length > 0);

    if (numbers.length === 0) {
      toast.error("No se encontraron números válidos");
      return;
    }

    setLoading(true);
    setResults([]);

    try {
      const { data } = await api.post("/verify-contacts", {
        phoneNumbers: numbers,
      });

      setResults(data.results);
      setStats({
        total: data.results.length,
        valid: data.results.filter((r) => r.exists).length,
        invalid: data.results.filter((r) => !r.exists).length,
      });

      toast.success(`Verificación completada: ${data.results.length} números procesados`);
    } catch (error) {
      console.error("Error verifying contacts:", error);
      toast.error("Error al verificar contactos. Por favor intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPhoneNumbers("");
    setResults([]);
    setStats({ total: 0, valid: 0, invalid: 0 });
  };

  const handleExport = () => {
    if (results.length === 0) {
      toast.warning("No hay resultados para exportar");
      return;
    }

    // Create CSV content
    const csvContent = [
      ["Número", "Estado", "Nombre"],
      ...results.map((r) => [
        r.phoneNumber,
        r.exists ? "Válido" : "Inválido",
        r.name || "N/A",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `verificacion_contactos_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Resultados exportados exitosamente");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Verificar Contactos de WhatsApp</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleVerify}
            disabled={loading || !phoneNumbers.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {loading ? "Verificando..." : "Verificar"}
          </Button>
          {results.length > 0 && (
            <>
              <Button variant="outlined" color="secondary" onClick={handleExport}>
                Exportar CSV
              </Button>
              <Button variant="outlined" onClick={handleClear}>
                Limpiar
              </Button>
            </>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.uploadSection}>
          <Typography variant="h6" gutterBottom>
            Ingresa los números de teléfono
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Ingresa un número por línea o separados por comas. Incluye el código de país (ej: +573001234567)
          </Typography>
          <TextField
            className={classes.textArea}
            multiline
            rows={10}
            variant="outlined"
            placeholder="Ejemplo:&#10;+573001234567&#10;+573009876543&#10;+5491123456789"
            value={phoneNumbers}
            onChange={(e) => setPhoneNumbers(e.target.value)}
            disabled={loading}
          />
        </Box>

        {stats.total > 0 && (
          <Grid container spacing={2} className={classes.statsGrid}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Verificados
                  </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Válidos (Con WhatsApp)
                  </Typography>
                  <Typography variant="h4" style={{ color: "#4caf50" }}>
                    {stats.valid}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Inválidos (Sin WhatsApp)
                  </Typography>
                  <Typography variant="h4" style={{ color: "#f44336" }}>
                    {stats.invalid}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginLeft: 16 }}>
              Verificando contactos...
            </Typography>
          </Box>
        )}

        {results.length > 0 && !loading && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Detalles</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.phoneNumber}</TableCell>
                    <TableCell>
                      {result.exists ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Válido"
                          className={classes.validChip}
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Inválido"
                          className={classes.invalidChip}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>{result.name || "N/A"}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {result.exists
                          ? "Este número tiene WhatsApp activo"
                          : "Este número no tiene WhatsApp o no existe"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </MainContainer>
  );
};

export default VerifyContacts;
